import { useEffect, useState } from 'react';
import { z } from 'zod';

import {
  EventScope,
  EventServiceProvider,
  useClickEvent,
  useInViewedEvent,
} from '@autopass-web/mochi-analytics/event';
import {
  DialogLayout,
  MochiButton,
  MochiCheckbox,
  MochiInput,
} from '@autopass-web/mochi-component';
import { CloseIcon } from '@autopass-web/mochi-icons';

type EventLogger = (eventName: string, data?: Record<string, unknown>) => void;

const emailSchema = z
  .string()
  .trim()
  .min(1, { message: '請輸入有效的電子郵件地址' })
  .email({ message: '請輸入有效的電子郵件地址' });

interface DpassSignupDialogProps {
  isDialogOpen: boolean;
  onDialogClose: () => void;
  onDialogSubmit: (email: string) => Promise<void> | void;
  isSubmitting?: boolean;
  logger: EventLogger;
  eventProps?: Record<string, string>;
}

const DPASS_SIGNUP_DIALOG_SCOPE = '加入駕駛中心提醒視窗';

const DpassSignupDialogContent = ({
  isDialogOpen,
  onDialogClose,
  onDialogSubmit,
  isSubmitting = false,
}: DpassSignupDialogProps) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const logInView = useInViewedEvent('提醒視窗');
  const logContinueClick = useClickEvent('繼續付款');
  const logCloseClick = useClickEvent('關閉視窗');
  const logCloseIconClick = useClickEvent('關閉視窗icon');

  useEffect(() => {
    if (isDialogOpen) {
      logInView({}, { key: 'dpass-signup-dialog' });
    }
  }, [isDialogOpen, logInView]);

  const handleConfirm = async () => {
    const parseResult = emailSchema.safeParse(email);

    if (!parseResult.success) {
      setErrorMessage(
        (parseResult as z.SafeParseError<string>).error.issues[0]?.message ??
          '請輸入有效的電子郵件地址',
      );
      return;
    }

    setErrorMessage(null);
    logContinueClick({ email });
    await onDialogSubmit(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <DialogLayout show={isDialogOpen}>
      <div className="mx-6 mt-10 mb-2">
        <CloseIcon
          onClick={() => {
            onDialogClose();
            logCloseIconClick();
          }}
          className="absolute top-3 right-[15px]"
        />
        <h1 className="text-xl font-bold text-center mb-4">
          完成資訊以繼續付款
        </h1>
        <p className="text-xs text-gray-500 mb-4">
          您已勾選加入駕駛中心，請填寫電子信箱以接收帳號驗證連結，點擊下方「繼續付款」按鈕，以完成本次交易。
        </p>
        <MochiInput
          variant="grey"
          inputMode="email"
          autoComplete="email"
          placeholder="輸入 Email 信箱"
          value={email}
          errorMessage={errorMessage}
          onChange={handleEmailChange}
        />
        <div className="flex flex-col gap-2 mt-6">
          <MochiButton
            type="button"
            variant="solid-round"
            size="full"
            disabled={!email || isSubmitting}
            isLoading={isSubmitting}
            onClick={handleConfirm}
            className="bg-[#171E2B] rounded-[10px]"
          >
            繼續付款
          </MochiButton>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              onDialogClose();
              logCloseClick();
            }}
            className="text-[#073A7E] font-bold my-2"
          >
            下次再加入
          </button>
        </div>
      </div>
    </DialogLayout>
  );
};

interface DpassSignupCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  dpassAppUrl: string;
  termsOfServiceUrl: string;
  privacyPolicyUrl: string;
}

const DpassSignupCheckbox = ({
  checked,
  onCheckedChange,
  dpassAppUrl,
  termsOfServiceUrl,
  privacyPolicyUrl,
}: DpassSignupCheckboxProps) => {
  return (
    <div className="flex items-center gap-2 pt-2 pb-3 px-5">
      <MochiCheckbox
        value="dpass-signup"
        checked={checked}
        onChange={(e) => {
          const checked = e.target.checked;
          onCheckedChange(checked);
        }}
      />
      <span className="text-xs">
        點擊下方按鈕同意加入
        <a
          className="underline text-black"
          href={dpassAppUrl}
          target="_blank"
          rel="noreferrer"
        >
          駕駛中心
        </a>
        並享有支付優惠服務且同意
        <a
          className="underline text-black"
          href={termsOfServiceUrl}
          target="_blank"
          rel="noreferrer"
        >
          服務條款
        </a>
        與
        <a
          className="underline text-black"
          href={privacyPolicyUrl}
          target="_blank"
          rel="noreferrer"
        >
          隱私權政策
        </a>
        。取消勾選後仍可完成本次交易。
      </span>
    </div>
  );
};

interface DpassSignupSectionProps
  extends DpassSignupCheckboxProps,
    DpassSignupDialogProps {}

const DpassSignupSection = ({
  checked,
  onCheckedChange,
  dpassAppUrl,
  termsOfServiceUrl,
  privacyPolicyUrl,
  isDialogOpen,
  onDialogClose,
  onDialogSubmit,
  isSubmitting,
  logger,
  eventProps,
}: DpassSignupSectionProps) => {
  return (
    <EventServiceProvider logger={logger}>
      <DpassSignupCheckbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        dpassAppUrl={dpassAppUrl}
        termsOfServiceUrl={termsOfServiceUrl}
        privacyPolicyUrl={privacyPolicyUrl}
      />
      <EventScope
        eventProps={{
          ...eventProps,
          scope: DPASS_SIGNUP_DIALOG_SCOPE,
        }}
      >
        <DpassSignupDialogContent
          isDialogOpen={isDialogOpen}
          isSubmitting={isSubmitting}
          onDialogClose={onDialogClose}
          onDialogSubmit={onDialogSubmit}
          logger={logger}
          eventProps={eventProps}
        />
      </EventScope>
    </EventServiceProvider>
  );
};

export { DpassSignupSection };
